import { ChangeDetectionStrategy, Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { UIAnnotation } from "@features/labeling/models/annotation";
import { UILabel } from "@features/labeling/models/label";
import { LabelingAnnotationService } from "@features/labeling/services/labeling-annotation.service";
import { LabelingColorService } from "@features/labeling/services/labeling-color.service";
import { LabelingService } from "@features/labeling/services/labeling.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { SimpleKeyValue } from "dku-frontend-core";
import { clone, cloneDeep } from "lodash";
import { Observable, ReplaySubject, Subject, withLatestFrom } from "rxjs";

@UntilDestroy()
@Component({
    selector: 'category-selector',
    templateUrl: './category-selector.component.html',
    styleUrls: [
        './category-selector.component.less',
        '../../shared-styles/right-panel.less',
        '../../shared-styles/annotation.less'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class CategorySelectorComponent implements OnInit, OnChanges {
    @Input() label: UILabel | null;
    @Output() labelChange = new EventEmitter<UILabel>();
    @Output() categoryChange = new EventEmitter<string>();

    private selectCategoryByIndex$ = new Subject<number>();
    displayedLabel: UILabel | null;
    selectedCategory$ = new ReplaySubject<string>(1);

    classes$: Observable<SimpleKeyValue[]>;

    constructor(private labelingColorService: LabelingColorService,
        private labelingAnnotationService: LabelingAnnotationService,
        public labelingService: LabelingService) {        
    }
    
    ngOnInit(): void {
        this.classes$ = this.labelingService.classesWithInstructions$;
        
        this.selectCategoryByIndex$.pipe(
            withLatestFrom(this.classes$),
            untilDestroyed(this),
        ).subscribe(([index, classes]) => {
            if (classes.length > index) {
                this.selectedCategory$.next(classes[index].key);       
            }
        });

        this.selectedCategory$.pipe(
            untilDestroyed(this)
        ).subscribe((category) => {
            this.selectCategory(category);
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.label) {
            this.displayedLabel = cloneDeep(this.label);
        }
    }

    categoryIsActive(selectedCategory: string | null, currentCategory: string) {
        return this.displayedLabel?.selectable ? selectedCategory === currentCategory : this.displayedLabel?.annotations[0]?.category === currentCategory;
    }

    categoryClicked(event: Event, category: string) {
        this.selectedCategory$.next(category);
        (event.currentTarget as HTMLButtonElement).blur();
    }

    colorStr(category: string, opacity?: number) {
        return this.labelingColorService.categoryToColorString(category, opacity);
    }

    @HostListener('window:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        const tagName = (event?.target as HTMLElement)?.tagName?.toLowerCase();
        const key = parseInt(event.key);
        if (!(tagName === 'input' || tagName === 'textarea' || isNaN(key))) {
            const classIndex = key !== 0 ? key - 1 : 9;
            this.selectCategoryByIndex$.next(classIndex);
        }
    }

    private selectCategory(category: string) {
        if(!this.displayedLabel) {
            return;
        }
        let updateLabel = true;

        if (this.displayedLabel.selectable) {
            this.labelingAnnotationService.selectCategory(category);
            const selectedAnnotations = this.displayedLabel.annotations.filter(a => a.selected);
            selectedAnnotations.forEach(a => a.category = category);
            // if nothing is selected, don't update label
            updateLabel = !!selectedAnnotations.length;
        } else {
            this.displayedLabel.annotations = [new UIAnnotation(category)];
        }

        if (updateLabel) {
            this.labelChange.emit(this.displayedLabel);
        }
        this.categoryChange.emit(category);
    }
}
